/**
 * The words and small sums of a team's or an agent's Overview (TeamOverview.tsx), apart from how it draws, so a view
 * of the same numbers elsewhere (the token economics to come) says them the same way.
 */

import { MEMBER_OVERVIEW_RANGES, type MemberOverview, type MemberOverviewRangeKey } from '@aioe/shared';

export const NOT_REPORTED = "Not reported yet by this team's machines";

export const RANGE_LABELS: Record<MemberOverviewRangeKey, string> = { '1h': '1h', '1d': '1d', '7d': '7d', '30d': '30d', '60d': '60d', '90d': '90d' };
export const RANGE_WORDS: Record<MemberOverviewRangeKey, string> = { '1h': 'the last hour', '1d': 'the last day', '7d': 'the last 7 days', '30d': 'the last 30 days', '60d': 'the last 60 days', '90d': 'the last 90 days' };
export const isRangeKey = (s: string | null | undefined): s is MemberOverviewRangeKey => !!s && (MEMBER_OVERVIEW_RANGES as readonly string[]).includes(s);

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** 1.2K, 14M: a count too big to read in full. */
export function compact(n: number): string {
  return new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: n < 10_000 ? 1 : 0 }).format(n);
}

/** A share as a whole percentage, or with one decimal near the edges (99.7%, 0.4%). */
export function percent(share: number): string {
  const p = share * 100;
  const digits = p > 0 && p < 1 ? 1 : p > 99 && p < 100 ? 1 : 0;
  return `${p.toFixed(digits)}%`;
}

/** How long something has waited: "18d 0h", "5h 12m", "40m", "just now". */
export function ageWords(ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60_000));
  if (m < 1) return 'just now';
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

/** The hour as the heatmap's axis says it: 00, 03 … 21. */
export const hourLabel = (h: number) => String(h).padStart(2, '0');

/** "Wednesday 14:00 to 15:00" */
export const cellWords = (day: number, hour: number) => `${WEEKDAY_NAMES[day]} ${hourLabel(hour)}:00 to ${hourLabel((hour + 1) % 24)}:00`;

/** Five steps of the heatmap's one-hue scale: 0 is none, 1 to 4 a quarter of the busiest hour each. */
export function heatStep(n: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (n <= 0 || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((n / max) * 4))) as 1 | 2 | 3 | 4;
}

/** The busiest hours, most first: what the heatmap says in words for a screen reader and the table view. */
export function busiest(h: MemberOverview['heatmap'], top = 5): Array<{ day: number; hour: number; calls: number }> {
  return h.cells
    .flatMap((row, day) => row.map((calls, hour) => ({ day, hour, calls })))
    .filter((c) => c.calls > 0)
    .sort((a, b) => b.calls - a.calls || a.day - b.day || a.hour - b.hour)
    .slice(0, top);
}

/** "3 active · 2 queued · 1 waiting" */
export const openWords = (o: MemberOverview['tiles']['open']) => `${o.active} active · ${o.queued} queued · ${o.waiting} waiting`;

/** The run-success detail: "359 of 361 runs · 1 failed · 1 handed back · 2 cancelled, not counted". */
export function runWords(r: MemberOverview['runSuccess']): string {
  const parts = [`${(r.succeeded).toLocaleString()} of ${plural(r.runs, 'run', 'runs')}`];
  if (r.failed) parts.push(`${r.failed} failed`);
  if (r.handedBack) parts.push(`${r.handedBack} handed back`);
  if (r.cancelled) parts.push(`${r.cancelled} cancelled, not counted`);
  return parts.join(' · ');
}

/** What a waiting item is, in a word or two, said before its title only where the title does not say it. */
export const WAITING_KIND_WORDS: Record<MemberOverview['waiting']['items'][number]['kind'], string> = {
  approval: 'Approval',
  question: 'Question',
  decision: 'Decision',
  blocked: 'Blocked',
  review: 'Review',
  other: 'Needs you',
};

/** Where a waiting item opens: its task on the project's board, else the Inbox. */
export function waitingLink(w: MemberOverview['waiting']['items'][number]): string {
  if (w.notificationId) return '/inbox?show=needs-you';
  return `/projects/${encodeURIComponent(w.projectId)}?tab=work&part=tasks`;
}
